import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import DOMPurify from 'dompurify'
import { getMediaUrl } from '../utils/media'
import { 
  ChevronLeft, 
  MapPin, 
  Calendar, 
  Users, 
  Briefcase, 
  Building2, 
  Loader2,
  Image as ImageIcon,
  CheckCircle2
} from 'lucide-react'

const DetailProjet = () => {
  const { id } = useParams()

  const { data: projet, isLoading, error } = useQuery({
    queryKey: ['projet', id],
    queryFn: async () => {
      const res = await api.get(`/projets/${id}`)
      return res.data
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-cidh-blue mb-4" size={50} />
        <p className="text-gray-500 font-medium">Chargement des détails du projet...</p>
      </div>
    )
  }

  if (error || !projet) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Oups ! Projet introuvable.</h2>
        <Link to="/projets" className="text-cidh-blue hover:underline flex items-center gap-2">
          <ChevronLeft size={20} /> Retour à la liste des projets
        </Link>
      </div>
    )
  }

  const coverImage = getMediaUrl(projet.miniature_image, 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200')

  const formatDate = (dateStr) => {
    if (!dateStr) return 'En cours';
    return dateStr; // Now using string dates from backend
  }

  return (
    <div className="pt-32 pb-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <Link to="/projets" className="inline-flex items-center gap-2 text-cidh-blue mb-8 hover:translate-x-1 transition-transform font-semibold">
          <ChevronLeft size={20} /> Retour à la liste des projets
        </Link>

        {/* Fiche Projet en format Tableau */}
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200">
          <div className="pt-12 pb-8 px-8 border-b bg-gray-50">
             <h1 className="text-2xl font-bold text-cidh-blue leading-tight mb-2">
               {projet.nom_mission}
             </h1>
          </div>

          <table className="w-full border-collapse text-sm md:text-base">
            <tbody>
              {/* Row 1 */}
              <tr>
                <td className="border p-6 w-1/2 align-top">
                  <span className="font-bold">Nom de la Mission :</span> {projet.nom_mission}
                </td>
                <td className="border p-6 w-1/2 align-top">
                  <span className="font-bold">Valeur approximative du contrat (en F CFA) :</span> <br/>
                  <span className="font-bold text-lg">{new Intl.NumberFormat('fr-FR').format(projet.valeur_contrat_fcfa)} F CFA</span>
                </td>
              </tr>

              {/* Row 2 */}
              <tr>
                <td className="border p-6 align-top">
                  <span className="font-bold">Pays :</span> {projet.pays} <br/>
                  <span className="font-bold">Lieu :</span> {projet.lieu}
                </td>
                <td className="border p-6 align-top">
                  <span className="font-bold">Durée de la mission (mois) :</span> {projet.duree_mission_mois} mois
                </td>
              </tr>

              {/* Row 3 */}
              <tr>
                <td className="border p-6 align-top">
                  <span className="font-bold">Nom de l’Autorité contractante :</span> <span className="font-bold">{projet.autorite_contractante}</span>
                </td>
                <td className="border p-6 align-top">
                  <span className="font-bold">Nombre total d’employés/mois ayant participé à la Mission :</span> <span className="font-bold">{projet.nb_employes_total < 10 ? `0${projet.nb_employes_total}` : projet.nb_employes_total}</span>
                </td>
              </tr>

              {/* Row 4 */}
              <tr>
                <td className="border p-6 align-top">
                   <span className="font-bold">Adresse :</span> {projet.adresse}
                </td>
                <td className="border p-6 align-top">
                  <span className="font-bold">Valeur approximative des services offerts par notre société (en F CFA) :</span> <br/>
                  <span className="font-bold">{new Intl.NumberFormat('fr-FR').format(projet.valeur_services_fcfa)} F CFA</span>
                </td>
              </tr>

              {/* Row 5 */}
               <tr>
                <td className="border p-6 align-top">
                   <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                      <div>
                        <span className="font-bold underline md:whitespace-nowrap">Date de démarrage (mois/année)</span><br/>
                        {formatDate(projet.date_demarrage)}
                      </div>
                      <div>
                        <span className="font-bold underline md:whitespace-nowrap">Date d’achèvement (mois/année)</span><br/>
                        {formatDate(projet.date_achevement)}
                      </div>
                   </div>
                </td>
                <td className="border p-6 align-top">
                  <span className="font-bold">Nombre d’employés/mois fournis par les consultants associés :</span> <span className="font-bold">{projet.nb_employes_associes < 10 ? `0${projet.nb_employes_associes}` : projet.nb_employes_associes}</span>
                </td>
              </tr>

              {/* Row 6: Experts & Cadres */}
              <tr>
                <td className="border p-6 align-top">
                   <span className="font-bold">Noms des consultants associés :</span><br/>
                   {projet.consultants_associes}
                </td>
                <td className="border p-6 align-top">
                  <span className="font-bold">Nom des cadres professionnels de notre Société employés et fonctions exécutées</span><br/><br/>
                  <div className="whitespace-pre-wrap">{projet.cadres_professionnels}</div>
                </td>
              </tr>

              {/* Row 7: Description Projet */}
              <tr>
                <td colSpan="2" className="border p-6 align-top bg-gray-50/50">
                  <span className="font-bold">Description du projet :</span>
                  <div 
                    className="mt-2 prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(projet.description_projet) }}
                  />
                </td>
              </tr>

              {/* Row 8: Services Rendus */}
              <tr>
                <td colSpan="2" className="border p-6 align-top bg-gray-50/50">
                  <span className="font-bold">Description des services effectivement rendus par notre personnel :</span>
                  <ul className="mt-2 list-disc ml-5 space-y-1 text-gray-700">
                    {projet.services_rendus?.map((service, idx) => (
                      <li key={idx}>{service}</li>
                    ))}
                  </ul>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Section Médias (Images & Vidéos) */}
        {(projet.images_realisations?.length > 0 || projet.videos_realisations?.length > 0) && (
          <div className="mt-12 space-y-8">
            <h2 className="text-2xl font-bold text-cidh-blue flex items-center gap-3">
              <ImageIcon className="text-cidh-gold" size={28} />
              Album du projet & Réalisations
            </h2>

            {/* Images */}
            {projet.images_realisations?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projet.images_realisations.map((img, idx) => (
                  <div key={idx} className="rounded-2xl overflow-hidden h-64 shadow-lg border border-gray-100 bg-gray-200">
                    <img 
                      src={getMediaUrl(img, 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800')} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      alt={`Photo ${idx + 1}`} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800';
                      }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Vidéos */}
            {projet.videos_realisations?.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-bold text-cidh-blue">Vidéos de réalisation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {projet.videos_realisations.map((video, idx) => (
                    <div key={idx} className="rounded-2xl overflow-hidden shadow-2xl bg-black aspect-video">
                      <video controls className="w-full h-full">
                        <source src={getMediaUrl(video, null)} type="video/mp4" />
                      </video>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DetailProjet
